import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../api/client';
import { generationApi, parseReview, type BlogRun } from '../../api/blog';
import BlogNav from './BlogNav';
import ReviewCard from './ReviewCard';

/** While something is being written the page asks again this often; otherwise it sits still. */
const POLL_MS = 3000;

const RUN_BADGE: Record<BlogRun['status'], string> = {
  Running: 'badge badge--info',
  Published: 'badge badge--published',
  Drafted: 'badge',
  NeedsAttention: 'badge badge--warning',
  Failed: 'badge badge--danger',
  Skipped: 'badge',
};

const money = (usd: number) => `$${usd.toFixed(2)}`;

function when(iso: string) {
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

function pretty(json: string | null) {
  if (!json) return '';

  try {
    return JSON.stringify(JSON.parse(json), null, 2);
  } catch {
    return json;
  }
}

/**
 * The generator's controls and its record.
 *
 * Articles are published with nobody reading them first, so this page has two jobs: to make it
 * obvious whether tomorrow's article will be written and, if not, exactly why; and to let somebody
 * stop it in one click, without a deploy, the moment something looks wrong.
 */
export default function BlogAutomationPage() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const [note, setNote] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const selectedId = params.get('run');

  const { data: status } = useQuery({ queryKey: ['blog-automation'], queryFn: generationApi.status });

  const { data: runs } = useQuery({
    queryKey: ['blog-runs', page],
    queryFn: () => generationApi.runs(page),
    refetchInterval: (query) => (query.state.data?.items.some((run) => run.status === 'Running') ? POLL_MS : false),
  });

  const { data: detail } = useQuery({
    queryKey: ['blog-run', selectedId],
    queryFn: () => generationApi.run(selectedId!),
    enabled: selectedId !== null,
    refetchInterval: (query) => (query.state.data?.run.status === 'Running' ? POLL_MS : false),
  });

  function fail(e: unknown) {
    setError(e instanceof ApiError ? e.message : 'Something went wrong.');
  }

  async function refresh() {
    setError(null);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['blog-automation'] }),
      queryClient.invalidateQueries({ queryKey: ['blog-runs'] }),
    ]);
  }

  const pause = useMutation({
    mutationFn: () => generationApi.pause(note.trim() || null),
    onSuccess: async () => {
      setNote('');
      await refresh();
    },
    onError: fail,
  });

  const resume = useMutation({ mutationFn: generationApi.resume, onSuccess: refresh, onError: fail });

  const started = async (run: BlogRun) => {
    setPage(1);
    setParams({ run: run.id });
    await refresh();
  };

  const generate = useMutation({ mutationFn: () => generationApi.generate(null), onSuccess: started, onError: fail });
  const plan = useMutation({ mutationFn: generationApi.plan, onSuccess: started, onError: fail });

  const spent = status ? Math.min(100, (status.monthSpendUsd / Math.max(status.monthlyBudgetUsd, 0.01)) * 100) : 0;
  const review = parseReview(detail?.reviewJson ?? null);

  return (
    <>
      <BlogNav />

      <section className="card">
        <h2>Automatic articles</h2>
        <p className="muted">
          One article a day at {String(status?.publishHourSofia ?? 8).padStart(2, '0')}:00 Sofia time, published
          without anyone reading it first. What stands in for the reader: every figure is checked against the active
          parameter set, a second model reviews the text, and anything doubtful is parked under{' '}
          <Link to="/blog?status=NeedsAttention">Needs attention</Link> instead of going live.
        </p>

        {error && <p className="status-error">{error}</p>}

        {status && (
          <>
            {status.blockers.length === 0 ? (
              <p className="status-ok">
                Nothing is in the way: the next sweep after {String(status.publishHourSofia).padStart(2, '0')}:00 writes
                today's article.
              </p>
            ) : (
              <div className="alert alert--warning">
                <strong>Today's automatic article will not be written, because:</strong>
                <ul>
                  {status.blockers.map((blocker) => (
                    <li key={blocker}>{blocker}</li>
                  ))}
                </ul>
              </div>
            )}

            <dl className="datalist">
              <dt>Model</dt>
              <dd>{status.model}</dd>
              <dt>Prompts</dt>
              <dd>{status.promptVersion}</dd>
              <dt>Topics waiting</dt>
              <dd>
                <Link to="/blog/topics">{status.queuedTopics}</Link>
                {status.queuedTopics < status.minTopicBacklog && <span className="muted"> · running low</span>}
              </dd>
              <dt>Parked for a person</dt>
              <dd>
                <Link to="/blog?status=NeedsAttention">{status.needsAttentionPosts}</Link>
              </dd>
              <dt>Spent in {status.monthKey}</dt>
              <dd>
                {money(status.monthSpendUsd)} of {money(status.monthlyBudgetUsd)}
                <span className="meter" aria-hidden="true">
                  <span className="meter__fill" style={{ width: `${spent}%` }} />
                </span>
              </dd>
            </dl>

            <div className="toolbar">
              {status.paused ? (
                <button type="button" className="btn btn--primary" disabled={resume.isPending} onClick={() => resume.mutate()}>
                  Resume
                </button>
              ) : (
                <>
                  <input
                    className="field__input"
                    style={{ maxWidth: '22rem' }}
                    placeholder="Why it is paused (optional)"
                    value={note}
                    maxLength={500}
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <button type="button" className="btn" disabled={pause.isPending} onClick={() => pause.mutate()}>
                    Pause
                  </button>
                </>
              )}

              <button
                type="button"
                className="btn"
                disabled={!status.configured || generate.isPending}
                title={status.configured ? 'Writes the next topic in the queue. Lands as a draft.' : 'No API key configured'}
                onClick={() => generate.mutate()}
              >
                Generate now
              </button>
              <button
                type="button"
                className="btn"
                disabled={!status.configured || plan.isPending}
                title="Asks the model for new topics and adds the ones not already covered"
                onClick={() => plan.mutate()}
              >
                Plan topics
              </button>
            </div>

            {!status.enabled && (
              <p className="muted">
                The daily run is switched off in configuration (<code>Blog:AutoGenerate:Enabled</code>). "Generate now"
                works without it, and is the way to read a few articles before switching it on.
              </p>
            )}
          </>
        )}
      </section>

      <section className="card">
        <h3>Runs</h3>

        {runs && runs.items.length === 0 && <p className="muted">Nothing has been generated yet.</p>}

        {runs && runs.items.length > 0 && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Kind</th>
                  <th>Outcome</th>
                  <th>Wrote</th>
                  <th>Score</th>
                  <th>Cost</th>
                  <th>Took</th>
                </tr>
              </thead>
              <tbody>
                {runs.items.map((run) => (
                  <tr
                    key={run.id}
                    className={run.id === selectedId ? 'is-selected' : undefined}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setParams({ run: run.id })}
                  >
                    <td>{when(run.startedAt)}</td>
                    <td>
                      {run.trigger}
                      {run.trigger === 'Auto' && run.attempt > 1 ? ` · attempt ${run.attempt}` : ''}
                    </td>
                    <td>
                      <span className={RUN_BADGE[run.status]}>
                        {run.status === 'Running' && run.stage ? run.stage : run.status}
                      </span>
                    </td>
                    <td>{run.postTitle ?? run.topicTitle ?? '—'}</td>
                    <td>{run.reviewScore ?? '—'}</td>
                    <td>{run.estimatedCostUsd > 0 ? money(run.estimatedCostUsd) : '—'}</td>
                    <td>{run.durationMs > 0 ? `${Math.round(run.durationMs / 1000)} s` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {runs && runs.totalPages > 1 && (
          <div className="toolbar">
            <button type="button" className="btn btn--small" disabled={!runs.hasPrevious} onClick={() => setPage(page - 1)}>
              Newer
            </button>
            <span className="muted">
              Page {runs.page} of {runs.totalPages}
            </span>
            <button type="button" className="btn btn--small" disabled={!runs.hasNext} onClick={() => setPage(page + 1)}>
              Older
            </button>
          </div>
        )}
      </section>

      {detail && (
        <section className="card">
          <h3>
            {detail.run.trigger} run · {when(detail.run.startedAt)}
          </h3>

          {detail.run.status === 'Running' && (
            <p className="muted">Working — {detail.run.stage ?? 'starting'}. This takes a few minutes; the page updates by itself.</p>
          )}

          {detail.run.error && <p className="status-error">{detail.run.error}</p>}

          <p className="muted">
            {detail.run.model} · prompts {detail.run.promptVersion} · {detail.run.inputTokens.toLocaleString()} tokens in (
            {detail.run.cacheReadTokens.toLocaleString()} from cache) · {detail.run.outputTokens.toLocaleString()} out ·{' '}
            {money(detail.run.estimatedCostUsd)}
          </p>

          {detail.run.postId && (
            <p>
              <Link className="btn btn--small" to={`/blog/${detail.run.postId}`}>
                Open the article
              </Link>
            </p>
          )}

          {review && <ReviewCard review={review} />}

          {detail.researchBrief && (
            <details>
              <summary>Research notes</summary>
              <pre className="code code--wrap">{detail.researchBrief}</pre>
            </details>
          )}

          {detail.writerOutputJson && (
            <details>
              <summary>What the model returned</summary>
              <pre className="code code--wrap">{pretty(detail.writerOutputJson)}</pre>
            </details>
          )}
        </section>
      )}
    </>
  );
}
